import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function TimeTracking({ user }) {
  const [currentEntry, setCurrentEntry] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    fetchCurrentEntry();
  }, []);

  useEffect(() => {
    if (!currentEntry) return;

    const interval = setInterval(() => {
      const start = new Date(currentEntry.clock_in);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      setElapsedTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentEntry]);

  const fetchCurrentEntry = async () => {
    try {
      const response = await authFetch(`${API}/time/current`);
      const data = await response.json();
      setCurrentEntry(data);
    } catch (error) {
      console.error('Error fetching current entry:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-in`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        setCurrentEntry(entry);
        toast.success('Clocked in successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-out`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        toast.success(`Clocked out! Duration: ${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m`);
        setCurrentEntry(null);
        setElapsedTime('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Failed to clock out');
    }
  };

  return (
    <Layout user={user}>
      <div data-testid="time-tracking-page">
        <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-6">Time Tracking</h1>

        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-12">
            <div className="text-center">
              <div className="mb-8">
                <p className="text-sm font-medium text-foreground-muted mb-4">
                  {currentEntry ? 'Currently Working' : 'Ready to start?'}
                </p>
                {currentEntry && (
                  <div className="bg-slate-50 rounded-xl px-8 py-6 inline-block mb-6">
                    <p className="text-6xl font-heading font-bold text-foreground-DEFAULT tracking-tight">
                      {elapsedTime}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                {currentEntry ? (
                  <button
                    data-testid="clock-out-button"
                    onClick={handleClockOut}
                    className="bg-destructive text-white rounded-full w-40 h-40 flex flex-col items-center justify-center text-2xl font-bold shadow-xl shadow-destructive/20 hover:scale-105 transition-transform"
                  >
                    <Clock className="w-10 h-10 mb-3" />
                    Clock Out
                  </button>
                ) : (
                  <button
                    data-testid="clock-in-button"
                    onClick={handleClockIn}
                    className="bg-accent text-white rounded-full w-40 h-40 flex flex-col items-center justify-center text-2xl font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform"
                  >
                    <Clock className="w-10 h-10 mb-3" />
                    Clock In
                  </button>
                )}
              </div>

              {currentEntry && (
                <div className="mt-8 text-left bg-slate-50 rounded-xl p-6">
                  <p className="text-sm text-foreground-muted mb-2">Started at</p>
                  <p className="text-lg font-semibold text-foreground-DEFAULT">
                    {new Date(currentEntry.clock_in).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default TimeTracking;